"use client";

import { useState, useCallback, useRef, useEffect } from "react";

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY;

declare global {
  interface Window {
    hcaptcha?: {
      render: (element: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      execute: (widgetId?: string) => Promise<string>;
      remove: (widgetId: string) => void;
    };
  }
}

export function useHCaptcha() {
  const [isReady, setIsReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!HCAPTCHA_SITE_KEY || !containerRef.current) {
      setIsReady(true);
      return;
    }

    const container = containerRef.current;

    const init = () => {
      try {
        widgetIdRef.current = window.hcaptcha!.render(container, {
          sitekey: HCAPTCHA_SITE_KEY,
          size: "invisible",
          callback: (newToken: string) => {
            setToken(newToken);
          },
          "expired-callback": () => {
            setToken(null);
          },
          "error-callback": () => {
            setError("Captcha error occurred");
          },
        });
        setIsReady(true);
      } catch (e) {
        console.error("hCaptcha init error:", e);
        setIsReady(true);
      }
    };

    if (window.hcaptcha) {
      init();
    } else {
      const check = setInterval(() => {
        if (window.hcaptcha) {
          clearInterval(check);
          init();
        }
      }, 100);
      const timeout = setTimeout(() => {
        clearInterval(check);
        setIsReady(true);
      }, 5000);
      return () => {
        clearInterval(check);
        clearTimeout(timeout);
      };
    }

    return () => {
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch {}
      }
    };
  }, []);

  const verify = useCallback(async (): Promise<boolean> => {
    // Fail OPEN when hCaptcha isn't configured/loaded: if the site key env is
    // missing or the script hasn't loaded, don't block registration — captcha
    // is simply inactive until VITE_HCAPTCHA_SITE_KEY is set in the build.
    // When the key IS configured, a genuine verification failure still blocks.
    if (!HCAPTCHA_SITE_KEY || !window.hcaptcha || !widgetIdRef.current) {
      return true;
    }

    setError(null);

    try {
      const newToken = await window.hcaptcha.execute(widgetIdRef.current);
      setToken(newToken);
      return true;
    } catch (e) {
      console.error("hCaptcha verify error:", e);
      setError("Captcha verification failed");
      return false;
    }
  }, []);

  const onVerify = useCallback((newToken: string) => {
    setToken(newToken);
  }, []);

  const onExpire = useCallback(() => {
    setToken(null);
  }, []);

  const onError = useCallback(() => {
    setError("Captcha error occurred");
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    setError(null);
    if (widgetIdRef.current && window.hcaptcha) {
      try {
        window.hcaptcha.reset(widgetIdRef.current);
      } catch {}
    }
  }, []);

  return {
    verify,
    token,
    error,
    reset,
    isReady,
    setIsReady,
    containerRef,
    onVerify,
    onExpire,
    onError,
  };
}